import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiCall, fmtDate } from "./calendarUtils";
import { DateField, TimeSelect } from "./DateField";
import { ProposalCard } from "../proposals/Proposals";

const inp = "h-10 w-full px-3 bg-[#0A0A0A] border border-white/20 text-white text-sm";
const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const wdName = (d) => WEEKDAYS[new Date(`${d}T12:00:00`).getDay()];

export function BlockChip({ b, onOpen, top, height }) {
  return (
    <button type="button" onClick={() => onOpen(b)} className="absolute inset-x-1 z-[5] bg-[#7f1d1d]/70 border border-red-500/50 text-[11px] text-red-100 px-1.5 text-left overflow-hidden"
      style={{ top, height, backgroundImage: "repeating-linear-gradient(135deg,transparent 0 6px,rgba(0,0,0,.25) 6px 12px)" }} data-testid={`time-block-${b.id}-${b.date}`}>
      <Lock className="inline w-3 h-3 mr-1" />{b.start}–{b.end} {b.reason || "Gesperrt"}
    </button>
  );
}

export function BlockList({ blocks, onOpen }) {
  if (!blocks.length) return null;
  return (
    <div className="mt-4 border border-red-500/30 bg-[#7f1d1d]/10 p-3" data-testid="time-block-list">
      <p className="text-sm text-white font-semibold mb-2 flex items-center gap-2"><Ban className="w-4 h-4 text-red-300" />Gesperrte Zeiten im Zeitraum</p>
      <div className="flex flex-wrap gap-2">{blocks.map((b) => (
        <button key={b.id + b.date} type="button" onClick={() => onOpen(b)} className="text-xs px-2 py-1 border border-red-500/40 text-red-100 hover:bg-red-500/10" data-testid={`time-block-item-${b.id}-${b.date}`}>
          {fmtDate(b.date)} {b.start}–{b.end}{b.recurring ? " · Serie" : ""}{b.reason ? ` · ${b.reason}` : ""}</button>))}</div>
    </div>
  );
}

export function TimeBlockDialog({ token, block, defaultDate, open, onClose, onSaved }) {
  const isNew = !block;
  const [f, setF] = useState(null);
  const [scope, setScope] = useState("single");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  useEffect(() => {
    if (!open) return;
    setProposal(null);
    setF(block ? { date: block.date, start_time: block.start, end_time: block.end, reason: block.reason || "", recurring: block.recurring, until: block.until || "" }
      : { date: defaultDate, start_time: "16:00", end_time: "17:00", reason: "", recurring: false, until: "" });
    setScope(block?.recurring ? "single" : "series"); setCheck(null);
  }, [open, block, defaultDate]);
  const body = () => ({ ...f, until: f.recurring && f.until ? f.until : null, recurring: block?.recurring && scope === "single" ? false : f.recurring });
  useEffect(() => {
    if (!f || !open) return;
    const t = setTimeout(() => apiCall(token, "/admin/time-blocks/check", { method: "POST", body: body() }).then(setCheck).catch((e) => setCheck({ ok: false, message: e.message, conflicts: [] })), 300);
    return () => clearTimeout(t);
  }, [f, scope, open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try {
      const payload = isNew ? body() : { ...body(), block_id: block.id, scope, occurrence_date: block.date };
      setProposal(await apiCall(token, "/admin/proposals", { method: "POST", body: { type: isNew ? "block_create" : "block_update", payload } }));
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const remove = async () => {
    try { setProposal(await apiCall(token, "/admin/proposals", { method: "POST", body: { type: "block_delete", payload: { block_id: block.id, scope, occurrence_date: block.date } } })); } catch (e) { toast.error(e.message); }
  };
  if (proposal) return (
    <Dialog open={open} onOpenChange={(o) => !o && (setProposal(null), onClose())}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white rounded-none max-w-md" data-testid="time-block-proposal">
        <DialogHeader><DialogTitle>Vorschau – bitte prüfen</DialogTitle></DialogHeader>
        <ProposalCard token={token} p={proposal} onDone={() => { setProposal(null); onSaved(); }} />
      </DialogContent>
    </Dialog>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white rounded-none max-w-md" data-testid="time-block-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Zeit sperren" : "Gesperrte Zeit"}</DialogTitle></DialogHeader>
        {!isNew && <p className="text-xs text-[#A1A1AA]" data-testid="time-block-info">{wdName(block.date)}, {fmtDate(block.date)} · {block.start}–{block.end} Uhr · {block.recurring ? `Wiederkehrend (jeden ${wdName(block.date)}${block.until ? ` bis ${fmtDate(block.until)}` : ""})` : "Einmalig"} · Grund: {block.reason || "–"}</p>}
        {block?.recurring && <div className="flex gap-2 text-sm" data-testid="time-block-scope">
          {[["single", "Nur diesen Termin ändern"], ["series", "Gesamte Serie ändern"]].map(([k, l]) => (
            <label key={k} className={`flex-1 border p-2 cursor-pointer ${scope === k ? "border-[#c1ff72]" : "border-white/15"}`}><input type="radio" checked={scope === k} onChange={() => setScope(k)} className="mr-2 accent-[#c1ff72]" data-testid={`time-block-scope-${k}`} />{l}</label>))}</div>}
        <div className="space-y-3">
          <DateField value={f.date} onChange={(v) => setF({ ...f, date: v })} testId="time-block-date" />
          <div className="flex gap-2 items-center text-sm"><span className="text-[#A1A1AA]">Von</span>
            <TimeSelect value={f.start_time} onChange={(v) => setF({ ...f, start_time: v })} testId="time-block-start" className={inp} />
            <span className="text-[#A1A1AA]">bis</span>
            <TimeSelect value={f.end_time} onChange={(v) => setF({ ...f, end_time: v })} testId="time-block-end" className={inp} /></div>
          <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Grund (optional), z. B. Privater Termin" className={inp} data-testid="time-block-reason" />
          {!(block?.recurring && scope === "single") && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.recurring} onChange={(e) => setF({ ...f, recurring: e.target.checked })} className="accent-[#c1ff72]" data-testid="time-block-recurring" />
            Wöchentlich wiederholen (jeden {f.date ? wdName(f.date) : "Wochentag"})</label>}
          {f.recurring && !(block?.recurring && scope === "single") && <div className="text-sm text-[#A1A1AA]">Enddatum (optional)<DateField value={f.until} onChange={(v) => setF({ ...f, until: v })} testId="time-block-until" /></div>}
          {check && !check.ok && <div className="border border-yellow-400/50 bg-yellow-400/10 p-3 text-xs text-yellow-200" data-testid="time-block-conflict"><p className="font-semibold">{check.message}</p>
            {check.conflicts?.slice(0, 6).map((c) => <p key={c}>{c}</p>)}</div>}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {!isNew && <Button variant="outline" onClick={remove} className="rounded-none border-red-400/50 text-red-300" data-testid="time-block-delete">{block.recurring && scope === "single" ? "Nur diesen Termin freigeben" : "Sperre löschen / Zeit freigeben"}</Button>}
          <Button disabled={busy || !check?.ok} onClick={save} className="rounded-none bg-[#c1ff72] hover:bg-[#c1ff72]/90 text-black" data-testid="time-block-save">{isNew ? "Sperren (Vorschau)" : "Speichern (Vorschau)"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
